import React, { useState, useEffect, useCallback } from 'react';
import { clustersAPI } from '../services/api';
import GroupAccessPanel from './GroupAccessPanel';

/**
 * "Teams — <namespace>": who can see one namespace BESIDES its Project's members.
 *
 * A namespace belongs to exactly one Rancher Project (a single annotation), so the only way
 * to give it to more than one team is a grant applied straight to the namespace. Groups are
 * granted several at a time and follow membership; individual users are the escape hatch.
 * Rows a group created show "via group X" — they are revoked through the group.
 */
export default function NamespaceTeamsModal({ clusterId, namespace, users, health, S, setMsg, onUserGranted, onClose }) {
  const [nsBindings, setNsBindings] = useState([]);
  const [nsBindingsLoading, setNsBindingsLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadNsBindings = useCallback(async () => {
    setNsBindingsLoading(true);
    try {
      const r = await clustersAPI.listNamespaceRoleBindings(clusterId, namespace);
      setNsBindings(r.data.bindings || []);
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Failed to load namespace grants.' });
    } finally { setNsBindingsLoading(false); }
  }, [clusterId, namespace, setMsg]);

  useEffect(() => { loadNsBindings(); }, [loadNsBindings]);

  const doGrantNamespace = async (userId) => {
    if (!userId) return;
    setBusy(true);
    try {
      await clustersAPI.addNamespaceRoleBinding(clusterId, namespace, {
        user_id: Number(userId), role: 'project-member',
      });
      await loadNsBindings();
      onUserGranted && (await onUserGranted());
      setMsg({ kind: 'ok', text: 'Access granted — they can now see this namespace directly.' });
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Could not grant access.' });
    } finally { setBusy(false); }
  };

  const doRevokeNamespace = async (bindingName) => {
    if (!window.confirm('Revoke this user’s direct access to the namespace?')) return;
    setBusy(true);
    try {
      await clustersAPI.removeNamespaceRoleBinding(clusterId, namespace, bindingName);
      await loadNsBindings();
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Could not revoke access.' });
    } finally { setBusy(false); }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.modal, width: 640, maxWidth: 'calc(100vw - 32px)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Teams — {namespace}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, marginBottom: 14, lineHeight: 1.5 }}>
          Give this namespace to one or more groups, or to individual users, without moving it
          out of its Project. A namespace can be in only one Project — this is how it reaches
          more than one team.
        </div>

        <GroupAccessPanel
          clusterId={clusterId}
          kind="namespace"
          targetId={namespace}
          S={S}
          setMsg={setMsg}
          onChanged={() => loadNsBindings()}
        />
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>USERS</div>

        {nsBindingsLoading ? (
          <div style={{ fontSize: 13, color: 'var(--muted-2)' }}>Loading…</div>
        ) : nsBindings.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--muted-2)', marginBottom: 10 }}>
            No user grants yet — only this namespace's Project members and the groups above can see it.
          </div>
        ) : (
          <table style={{ ...S.table, marginBottom: 12 }}>
            <thead>
              <tr>
                <th style={S.th}>Platform user</th>
                <th style={S.th}>Role</th>
                <th style={S.th} />
              </tr>
            </thead>
            <tbody>
              {nsBindings.map(b => (
                <tr key={b.name}>
                  <td style={S.td}>
                    {users.find(u => health[u.id]?.rancher_user_id === b.rancher_user_id)?.username
                      || b.rancher_user_id}
                    <span style={{ color: 'var(--muted-2)', marginLeft: 8, fontSize: 11 }}>
                      {b.rancher_user_id}
                    </span>
                  </td>
                  <td style={S.td}>{b.role}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    {b.via_group ? (
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}
                        title="Managed by the group grant above — revoke it there, or remove the person from the group">
                        via group {b.via_group}
                      </span>
                    ) : (
                      <button style={S.btnDanger} onClick={() => doRevokeNamespace(b.name)} disabled={busy}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={S.toolbar}>
          <select
            style={S.select}
            defaultValue=""
            onChange={e => { doGrantNamespace(e.target.value); e.target.value = ''; }}
            disabled={busy}
          >
            <option value="">Add a user…</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.username}
                {health[u.id]?.healthy === false ? '  (access broken — re-add to fix)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button style={S.btn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
